import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Sun, Moon, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SleepScheduleGuide() {
  const navigate = useNavigate();

  const scheduleSteps = [
    { step: 1, title: "Calculate Your Ideal Bedtime", desc: "Work backwards from your wake time. If you need to wake at 6:30 AM and want 8 hours, bedtime is 10:30 PM." },
    { step: 2, title: "Set Two Alarms", desc: "One for bedtime (30 min warning) and one for wake time. Treat bedtime as seriously as a morning alarm." },
    { step: 3, title: "Stick to It—Even Weekends", desc: "Sleeping in disrupts your circadian rhythm. Max 1 hour variation on weekends." },
    { step: 4, title: "Give It Time", desc: "It takes 1-2 weeks for your body to adjust. Don't give up after a few nights." }
  ];

  const commonMistakes = [
    { mistake: "Sleeping in on weekends", why: "'Social jet lag' makes Monday mornings brutal and disrupts your whole week" },
    { mistake: "Going to bed too early", why: "If you're not tired, you'll lie awake and associate bed with frustration" },
    { mistake: "Inconsistent bedtime", why: "Your body can't establish a rhythm if times vary wildly" },
    { mistake: "Ignoring natural tiredness", why: "When you feel sleepy, go to bed—don't push through to 'finish one more thing'" }
  ];

  const sleepNeeds = [
    { hours: "7 hours", type: "Minimum for most adults" },
    { hours: "7-9 hours", type: "Recommended range" },
    { hours: "8-9 hours", type: "Ideal for recovery and wellness" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">
            Week 5 • Day 2 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Sleep Schedule Reset
          </h1>
          <p className="text-xl text-secondary-600">Train Your Body to Sleep Better</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Clock className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Task</h3>
              <p className="text-blue-100 text-lg">
                Choose your ideal bedtime and wake time (allowing 7-8 hours). Set alarms for both. 
                Stick to these times starting TODAY—even if you don't feel tired at bedtime.
              </p>
            </div>
          </div>
        </section>

        {/* Why Consistency Matters */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Consistency Matters</h2>
          <p className="text-secondary-700 mb-4">
            Your body has an internal clock (circadian rhythm) that regulates sleepiness and alertness. 
            When you keep consistent sleep times, this clock becomes stronger, making it easier to fall asleep 
            and wake up naturally. Inconsistency confuses your body and leads to poor sleep quality.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {sleepNeeds.map((item, i) => (
              <div key={i} className="text-center p-4 bg-blue-50 rounded-xl">
                <p className="text-2xl font-bold text-blue-600">{item.hours}</p>
                <p className="text-sm text-secondary-600">{item.type}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">How to Reset Your Schedule</h2>
          <div className="space-y-4">
            {scheduleSteps.map((item) => (
              <div key={item.step} className="bg-white rounded-xl p-4 flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-bold text-secondary-900">{item.title}</h3>
                  <p className="text-secondary-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Common Mistakes */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Mistakes to Avoid
          </h2>
          <div className="space-y-4">
            {commonMistakes.map((item, index) => (
              <div key={index} className="p-4 bg-amber-50 rounded-xl">
                <p className="font-bold text-amber-700 mb-1">{item.mistake}</p>
                <p className="text-sm text-secondary-600">{item.why}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SleepScheduleGuide;
